import React, { useEffect, useState, useCallback } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import API_BASE_URL from '../../../config/api';
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

function ExpertsContainer() {
  const { user } = useAuth();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);

  // New state variables for search and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 9,
        search: debouncedSearch
      });

      const response = await axios.get(`${API_BASE_URL}/api/experts?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });
      const data = await response.data;

      if (response.status === 200) {
        setExperts(data.experts || []);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage]);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 on search change
  }, [debouncedSearch]);

  useEffect(() => {
    fetchExperts(); // Fetch experts when component mounts or dependencies change
  }, [fetchExperts]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className='min-h-screen p-6 md:p-12'>
      <div className='max-w-7xl mx-auto'>

        <div className='flex flex-col gap-8'>

          {/* Search Bar */}
          <div className='bg-white px-4 py-2 rounded-full shadow-sm transition-all duration-300 hover:shadow-md focus-within:shadow-md flex items-center gap-3 max-w-7xl mx-auto w-full'>
            <FaSearch className='text-gray-400 text-lg' />
            <input
              type="text"
              placeholder="Search by expert name or company..."
              className="w-full text-gray-700 bg-transparent outline-none border-none focus:ring-0 placeholder-gray-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Experts Cards Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-h-[400px] content-start'>
            {loading ? (
              <div className='col-span-full flex flex-col items-center justify-center py-12 text-gray-500 font-medium'>
                <div className="w-10 h-10 border-4 border-gray-200 border-t-[#b7202e] rounded-full animate-spin mb-4"></div>
                Loading industry experts...
              </div>
            ) : experts.length === 0 ? (
              <div className='col-span-full text-center py-12 text-gray-500 font-medium bg-white rounded-xl p-8 shadow-sm'>
                <div className="text-4xl mb-3">🔍</div>
                No industry experts found matching your criteria.
              </div>
            ) : (
              experts.map((expert, index) => (
                <div
                  key={index}
                  className='bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col items-center relative overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-lg hover:shadow-red-900/10 group pt-8'
                >
                  {/* Top Border */}
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-[#b7202e]"></div>

                  <div className="mb-4 relative">
                    <div className="w-32 h-32 rounded-full border-4 border-white shadow-md bg-gradient-to-br from-rose-50 to-pink-100 flex items-center justify-center">
                      <span className="text-black text-3xl font-bold select-none"> {expert.name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()} </span>
                    </div>
                  </div>

                  <div className='text-xl font-bold text-[#b7202e] mb-2 text-center group-hover:scale-105 transition-transform duration-200'>
                    {expert.name}
                  </div>

                  <div className='inline-block px-4 py-1.5 bg-[#b7202e]/10 text-[#b7202e] rounded-full text-sm font-medium mb-3'>
                    {expert.industry}
                  </div>

                  <div className='text-sm text-gray-500 break-all text-center'>
                    {expert.email}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <div className='flex justify-center items-center gap-4 mt-8'>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className='p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-[#b7202e] hover:border-[#b7202e] disabled:opacity-50 disabled:cursor-not-allowed transition-all'
              >
                <FaChevronLeft />
              </button>

              <span className='font-medium text-gray-600'>
                Page <span className='text-[#b7202e] font-bold'>{currentPage}</span> of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className='p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-[#b7202e] hover:border-[#b7202e] disabled:opacity-50 disabled:cursor-not-allowed transition-all'
              >
                <FaChevronRight />
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ExpertsContainer;
